import React, { useEffect, useState, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import api from '../utils/api';

const STATUS_COLOR = { ready: '#22c55e', authenticated: '#facc15', qr_ready: '#f97316', disconnected: '#ef4444' };
const STATUS_LABEL = { ready: 'Live', authenticated: 'Auth OK', qr_ready: 'Scan QR', disconnected: 'Offline' };

// Feature → nav link mapping
const FEATURE_ROUTES = {
  dashboard:  { to: '/app',              label: 'Dashboard',        icon: '🏠', end: true },
  whatsapp:   { to: '/app/bot-config',   label: 'Bot & WhatsApp',   icon: '🤖' },
  chats:      { to: '/app/chats',        label: 'Live Chats',       icon: '💬' },
  leads:      { to: '/app/leads',        label: 'Leads',            icon: '📋' },
  followups:  { to: '/app/follow-ups',   label: 'Follow-Ups',       icon: '📅' },
  users:      { to: '/app/users',        label: 'Users & Licenses', icon: '⚙️' },
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { waStatus } = useSocket();
  const navigate = useNavigate();
  const [orgLogo, setOrgLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef(null);

  const color = STATUS_COLOR[waStatus] || '#ef4444';
  const label = STATUS_LABEL[waStatus] || 'Offline';

  const features = user?.allowedFeatures || ['dashboard', 'whatsapp', 'chats'];
  const links = features.filter(f => FEATURE_ROUTES[f]).map(f => FEATURE_ROUTES[f]);
  const isOrgAdmin = user?.role === 'orgadmin';
  const allowedApps = user?.allowedApps || ['bot'];
  const hasBroadcaster = allowedApps.includes('broadcaster');
  const hasSupportAgent = allowedApps.includes('support_agent');

  useEffect(() => {
    if (user?.role === 'orgadmin' || user?.role === 'user') {
      api.get('/api/org/my-org').then(({ data }) => { if (data.logo) setOrgLogo(data.logo); }).catch(() => {});
    }
  }, [user]);

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const { data } = await api.put('/api/org/my-org/logo', formData);
      setOrgLogo(data.logo);
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed');
    }
    setUploading(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  }

  return (
    <aside style={s.sidebar}>
      <div style={s.logoArea}>
        {orgLogo ? (
          <img src={orgLogo} alt="org logo" style={s.orgLogoImg} />
        ) : (
          <img src="/maxxcom-logo.png" alt="MAXXCOM" style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.logoText}>MAXXCOM</div>
          <div style={{ fontSize: 9, color: '#475569', letterSpacing: 0.3 }}>by AcceleratorX</div>
          {isOrgAdmin && <div style={s.roleTag}>Org Admin</div>}
        </div>
        {isOrgAdmin && (
          <>
            <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
            <button
              title="Upload org logo"
              onClick={() => logoInputRef.current.click()}
              style={s.logoUploadBtn}
              disabled={uploading}
            >
              {uploading ? '⏳' : '🖼️'}
            </button>
          </>
        )}
      </div>

      {(hasBroadcaster || hasSupportAgent) && (
        <div style={s.appSwitcher}>
          <div style={{ ...s.appTab, ...s.appTabActive }}>🤖 Bot</div>
          {hasBroadcaster && (
            <a
              href="https://broadcaster.maxxcom.in"
              style={s.appTab}
              title="Switch to Broadcaster"
            >
              📢 Broadcast
            </a>
          )}
          {hasSupportAgent && (
            <a
              href="/support-agent"
              style={s.appTab}
              title="Switch to Support Agent"
            >
              🎫 Support
            </a>
          )}
        </div>
      )}

      <div style={s.waStatus}>
        <span style={{ ...s.dot, background: color }} />
        <span style={{ fontSize: 12, color: '#94a3b8' }}>WhatsApp: {label}</span>
      </div>

      <nav style={s.nav}>
        {links.map(l => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            style={({ isActive }) => ({ ...s.link, ...(isActive ? s.linkActive : {}) })}
          >
            <span style={{ fontSize: 15 }}>{l.icon}</span>
            {l.label}
          </NavLink>
        ))}
        {isOrgAdmin && (
          <NavLink
            to="/app/integrations"
            style={({ isActive }) => ({ ...s.link, ...(isActive ? s.linkActive : {}) })}
          >
            <span style={{ fontSize: 15 }}>🔌</span>
            Integrations
          </NavLink>
        )}
      </nav>

      <div style={s.footer}>
        <p style={s.adminName}>{user?.name || user?.email}</p>
        <p style={s.adminEmail}>{user?.email}</p>
        <button style={s.logoutBtn} onClick={logout}>Logout</button>
      </div>
    </aside>
  );
}

const s = {
  sidebar: { width: 220, minHeight: '100vh', background: '#1e293b', borderRight: '1px solid #334155', display: 'flex', flexDirection: 'column', padding: '20px 12px' },
  logoArea: { display: 'flex', alignItems: 'center', gap: 8, padding: '0 4px 20px', borderBottom: '1px solid #334155' },
  orgLogoImg: { width: 36, height: 36, borderRadius: 8, objectFit: 'contain', background: '#fff', padding: 2, flexShrink: 0 },
  logoText: { fontWeight: 700, fontSize: 15, color: '#f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  roleTag: { fontSize: 10, color: '#22c55e', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' },
  logoUploadBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, padding: '2px 4px', flexShrink: 0, opacity: 0.6, lineHeight: 1 },
  waStatus: { display: 'flex', alignItems: 'center', gap: 6, padding: '12px 8px', marginBottom: 8 },
  dot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  appSwitcher: { display: 'flex', gap: 4, padding: '8px 4px', marginBottom: 4, borderBottom: '1px solid #334155' },
  appTab: { flex: 1, textAlign: 'center', padding: '6px 4px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#64748b', textDecoration: 'none', border: '1px solid transparent' },
  appTabActive: { background: '#334155', color: '#f97316', border: '1px solid #f9731640' },
  nav: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  link: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, fontSize: 14, color: '#94a3b8', textDecoration: 'none' },
  linkActive: { background: '#334155', color: '#f1f5f9', fontWeight: 600 },
  footer: { borderTop: '1px solid #334155', paddingTop: 16 },
  gatewayBtn: { width: '100%', padding: '8px', background: 'transparent', border: '1px solid #f9731640', borderRadius: 8, color: '#f97316', cursor: 'pointer', fontSize: 13, marginBottom: 10 },
  adminName: { fontSize: 13, color: '#f1f5f9', padding: '0 8px', marginBottom: 2, fontWeight: 600 },
  adminEmail: { fontSize: 11, color: '#64748b', padding: '0 8px', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  logoutBtn: { width: '100%', padding: '8px', background: 'transparent', border: '1px solid #334155', borderRadius: 8, color: '#94a3b8', cursor: 'pointer', fontSize: 13 },
};
